import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/contexts/ProfileContext";

interface GlobalCounters {
  savedUnique: number;
  heartUnique: number;
  visitorsUnique: number;
  updatedAt: string;
}

function getOrCreateAnonId(): string {
  const key = "memnora_anon_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  document.cookie = `anon_id=${id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  return id;
}

function ensureDeviceId(): string {
  const key = "memnoraDeviceId";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

export function useGlobalCounters() {
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const sseRef = useRef<EventSource | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sseConnected, setSseConnected] = useState(false);

  useEffect(() => {
    getOrCreateAnonId();
    ensureDeviceId();
  }, []);

  const { data: counters, isLoading } = useQuery<GlobalCounters>({
    queryKey: ["/api/global/counters"],
    refetchInterval: sseConnected ? false : 45_000,
  });

  useEffect(() => {
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function connect() {
      if (disposed) return;
      const es = new EventSource("/api/global/counters/stream");
      sseRef.current = es;

      es.onopen = () => {
        retryCount = 0;
        setSseConnected(true);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as GlobalCounters;
          queryClient.setQueryData(["/api/global/counters"], data);
        } catch (e) {
          console.warn("[SSE] Failed to parse counter update:", e);
        }
      };

      es.onerror = () => {
        setSseConnected(false);
        es.close();
        sseRef.current = null;

        if (!pollRef.current && !disposed) {
          pollRef.current = setInterval(() => {
            queryClient.invalidateQueries({ queryKey: ["/api/global/counters"] });
          }, 45_000);
        }

        if (!disposed) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 60_000) + Math.random() * 1000;
          retryCount++;
          retryTimer = setTimeout(connect, delay);
        }
      };
    }

    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [queryClient]);

  const buildHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (profile?.id) {
      h["X-Profile-Id"] = profile.id;
    }
    const deviceId = localStorage.getItem("memnoraDeviceId");
    if (deviceId) {
      h["X-Device-Id"] = deviceId;
    }
    return h;
  }, [profile?.id]);

  const actionMutation = useMutation({
    mutationFn: async (action: "declare_salvation" | "heart") => {
      const res = await fetch("/api/global/actions", {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ action }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to record action");
      }
      return res.json() as Promise<GlobalCounters & { isNew: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/global/counters"], {
        savedUnique: data.savedUnique,
        heartUnique: data.heartUnique,
        visitorsUnique: data.visitorsUnique ?? (counters?.visitorsUnique ?? 0),
        updatedAt: data.updatedAt,
      });
    },
  });

  const recordSaved = useCallback(() => actionMutation.mutate("declare_salvation"), [actionMutation]);
  const recordHeart = useCallback(() => actionMutation.mutate("heart"), [actionMutation]);

  return {
    savedUnique: counters?.savedUnique ?? 0,
    heartUnique: counters?.heartUnique ?? 0,
    visitorsUnique: counters?.visitorsUnique ?? 0,
    isLoading,
    recordSaved,
    recordHeart,
    isPending: actionMutation.isPending,
  };
}

export function useGlobalCounterActions() {
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  useEffect(() => {
    ensureDeviceId();
  }, []);

  const buildHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (profile?.id) {
      h["X-Profile-Id"] = profile.id;
    }
    const deviceId = localStorage.getItem("memnoraDeviceId");
    if (deviceId) {
      h["X-Device-Id"] = deviceId;
    }
    return h;
  }, [profile?.id]);

  const actionMutation = useMutation({
    mutationFn: async (action: "declare_salvation" | "heart") => {
      const res = await fetch("/api/global/actions", {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ action }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to record action");
      }
      return res.json() as Promise<GlobalCounters & { isNew: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/global/counters"], (prev: GlobalCounters | undefined) => ({
        savedUnique: data.savedUnique,
        heartUnique: data.heartUnique,
        visitorsUnique: data.visitorsUnique ?? (prev?.visitorsUnique ?? 0),
        updatedAt: data.updatedAt,
      }));
    },
  });

  const recordSaved = useCallback(() => actionMutation.mutate("declare_salvation"), [actionMutation]);
  const recordHeart = useCallback(() => actionMutation.mutate("heart"), [actionMutation]);

  return { recordSaved, recordHeart, isPending: actionMutation.isPending };
}
